import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, Link } from "react-router-dom"
import api from "../services/api"
import GradientText from "../components/GradientText/GradientText.jsx"
import "../components/GradientText/GradientText.css"

export default function SignupPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  // Timeline ticker for feature scenarios (0 to 11 seconds)
  const [ticker, setTicker] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setTicker((t) => (t + 1) % 12)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const rainbowColors = useMemo(() => ["#FF9E4A", "#F83CBD", "#9E4AFF", "#1F7EFB", "#4AE6FF"], [])

  const handleSignup = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long")
      setIsLoading(false)
      return
    }

    if (formData.password.length > 72) {
      setError("Password cannot be longer than 72 characters")
      setIsLoading(false)
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match")
      setIsLoading(false)
      return
    }

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      }

      await api.post("/users", payload)

      setTimeout(() => {
        navigate("/login")
      }, 2000)

    } catch (err) {
      console.error("Registration failed:", err)
      setError(err.response?.data?.detail || "Registration failed. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleGoogleLogin = () => {
    console.log("Google registration clicked")
  }

  const handleGithubLogin = () => {
    console.log("GitHub registration clicked")
  }

  // --- Dynamic Scenarios based on Timeline Ticker ---
  
  // 1. Task Card: Task gets checked off from 3s onwards
  const isTaskChecked = ticker >= 3 && ticker < 10

  // 2. Attendance Card: Rate jumps from 72% to 86.4% at 4s when user "Attends Class"
  const attendanceRate = ticker >= 4 && ticker < 11 ? 86.4 : 72.0
  const attendanceColor = ticker >= 4 && ticker < 11 ? "text-emerald-400" : "text-amber-500"

  // 3. Quiz Card: Selects the correct MCQ choice starting at 6s
  const isQuizAnswered = ticker >= 6 && ticker < 11

  // 4. Notes Card: Uploads lecture files (0-5s) and creates a summary (6s onwards)
  const notesProgress = ticker <= 2 ? 10 : ticker === 3 ? 40 : ticker === 4 ? 75 : 100
  const isNotesDone = ticker >= 6

  return (
    <div className="h-[100dvh] w-full flex flex-col md:flex-row bg-[#F8FAFC] text-[#0F172A] overflow-hidden"
      style={{ fontFamily: "'Figtree', system-ui, -apple-system, sans-serif" }}
    >
      
      {/* ===== Left Side: Brand & Feature Showcase (Hidden on Mobile) ===== */}
      <section className="hidden md:flex md:w-[45%] lg:w-[42%] bg-[#090D16] text-[#E2E8F0] p-10 flex-col justify-between relative overflow-hidden shrink-0 h-full border-r border-slate-800">
        
        {/* Glow Blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[80%] aspect-square rounded-full mix-blend-multiply filter blur-[120px] opacity-35 bg-purple-900/40" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[70%] aspect-square rounded-full mix-blend-multiply filter blur-[120px] opacity-30 bg-blue-900/30" />
        </div>

        {/* Top Header Logo */}
        <div className="flex items-center gap-3 z-10 cursor-pointer" onClick={() => navigate("/")}>
          <div className="flex items-center justify-center h-8.5 w-8.5 rounded-none font-extrabold text-sm bg-gradient-to-tr from-[#FF8D3A] via-[#F254B6] to-[#2D7FF7] text-white shadow-md">
            A
          </div>
          <GradientText
            className="text-xl font-extrabold tracking-tight"
            colors={rainbowColors}
            animationSpeed={8}
          >
            AutomateU
          </GradientText>
        </div>

        {/* Headline & UI Previews */}
        <div className="flex flex-col gap-5 my-auto z-10 w-full max-w-md">
          <div className="flex flex-col gap-1.5 text-left">
            <h2 className="text-3xl font-extrabold tracking-tight leading-[1.1] mb-1.5">
              Streamline your studies, <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF8D3A] via-[#F254B6] to-[#2D7FF7]">
                automate your success.
              </span>
            </h2>
            <p className="text-slate-400 text-xs leading-relaxed">
              Create your account to start optimizing your study workflow. Manage assignments, quizzes, and course metrics.
            </p>
          </div>

          {/* Interactive Feature Cards Grid */}
          <div className="grid grid-cols-2 gap-3.5 w-full">
            
            {/* Card 1: To Do / Tasks */}
            <div className="border border-white/5 bg-white/[0.02] p-4 rounded-none text-left flex flex-col justify-between min-h-[110px] transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="p-1 rounded-none bg-pink-500/10 text-pink-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">To-Do</span>
                </div>
                {isTaskChecked && <span className="text-[9px] font-bold text-pink-400 animate-pulse">Updated</span>}
              </div>
              <div className="flex flex-col gap-1.5 mt-2">
                <div className="flex items-center gap-1.5 text-xs text-slate-355">
                  <span className="text-emerald-400 font-bold">✓</span>
                  <span className="line-through opacity-40">Lab Submission</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-300">
                  <span className={isTaskChecked ? "text-emerald-400 font-bold" : "text-slate-500"}>
                    {isTaskChecked ? "✓" : "□"}
                  </span>
                  <span className={isTaskChecked ? "line-through opacity-40" : ""}>Physics Quiz</span>
                </div>
              </div>
            </div>

            {/* Card 2: Attendance Tracking */}
            <div className="border border-white/5 bg-white/[0.02] p-4 rounded-none text-left flex flex-col justify-between min-h-[110px] transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="p-1 rounded-none bg-emerald-500/10 text-emerald-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2" />
                    </svg>
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Attendance</span>
                </div>
                {attendanceRate === 86.4 && <span className="text-[9px] font-bold text-emerald-400 animate-pulse">Attended ✓</span>}
              </div>
              <div className="flex flex-col gap-1.5 mt-2">
                <div className="flex justify-between items-center text-xs font-semibold">
                  <span className="text-slate-400">Target Met</span>
                  <span className={`${attendanceColor} transition-all duration-505 font-extrabold`}>{attendanceRate}%</span>
                </div>
                <div className="w-full h-1 bg-slate-800 rounded-none overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-emerald-400 to-teal-500 h-full transition-all duration-700" 
                    style={{ width: `${attendanceRate}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Card 3: MCQ / Quiz */}
            <div className="border border-white/5 bg-white/[0.02] p-4 rounded-none text-left flex flex-col justify-between min-h-[110px] transition-all duration-305">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="p-1 rounded-none bg-amber-500/10 text-amber-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">MCQ Quiz</span>
              </div>
                {isQuizAnswered && <span className="text-[9px] font-bold text-emerald-400 animate-pulse">Correct</span>}
              </div>
              <div className="flex flex-col gap-1 mt-2 text-xs">
                <span className="font-semibold text-[10px] text-slate-350 truncate">Q: What is SQL?</span>
                <span className={`text-[10px] px-1.5 py-0.5 border ${
                  isQuizAnswered 
                    ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-400 font-extrabold" 
                    : "bg-white/[0.01] border-white/5 text-slate-400"
                } transition-all duration-300`}>
                  {isQuizAnswered ? "✓ A) Query Language" : "○ A) Query Language"}
                </span>
              </div>
            </div>

            {/* Card 4: AI Notes */}
            <div className="border border-white/5 bg-white/[0.02] p-4 rounded-none text-left flex flex-col justify-between min-h-[110px] transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="p-1 rounded-none bg-indigo-500/10 text-indigo-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6M9 16h6M7 8h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V10a2 2 0 012-2z" />
                    </svg>
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">AI Notes</span>
                </div>
                {isNotesDone && <span className="text-[9px] font-bold text-indigo-400 animate-pulse">Ready</span>}
              </div>
              <div className="flex flex-col gap-1 mt-2 text-xs text-slate-300">
                {isNotesDone ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-bold text-[10px] text-indigo-400 truncate">Summary Generated</span>
                    <span className="opacity-60 text-[9px]">Physics Lect 4.pdf</span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] text-slate-500">Processing audio...</span>
                    <div className="w-full h-1 bg-slate-800 rounded-none overflow-hidden">
                      <div className="bg-indigo-500 h-full transition-all duration-500" style={{ width: `${notesProgress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="text-[10px] font-bold tracking-wider uppercase text-slate-550 z-10">
          © 2026 AutomateU
        </div>
      </section>

      {/* ===== Right Side: Signup Form ===== */}
      <section className="flex-grow flex items-center justify-center p-4 sm:p-8 md:p-12 relative overflow-hidden h-full">
        
        {/* Mobile Background Blob Overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 md:hidden">
          <div className="absolute top-[-10%] left-[-10%] w-[70%] aspect-square rounded-full mix-blend-multiply filter blur-[80px] opacity-35 bg-purple-200/50" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[60%] aspect-square rounded-full mix-blend-multiply filter blur-[80px] opacity-30 bg-blue-200/50" />
        </div>

        <div className="w-full max-w-[380px] flex flex-col z-10 scale-[0.92] sm:scale-100 origin-center transition-transform">
          
          {/* Mobile Header Logo */}
          <div className="flex md:hidden items-center justify-center gap-2 mb-3">
            <div className="flex items-center justify-center h-7 w-7 rounded-none font-extrabold text-xs bg-gradient-to-tr from-[#FF8D3A] via-[#F254B6] to-[#2D7FF7] text-white shadow-md">
              A
            </div>
            <GradientText
              className="text-xl font-black tracking-tight"
              colors={rainbowColors}
              animationSpeed={8}
            >
              AutomateU
            </GradientText>
          </div>

          {/* Headline & Title */}
          <div className="text-left mb-4">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight">Create an account</h1>
            <p className="text-slate-555 text-[11px] sm:text-xs mt-1 font-semibold">Join us to automate your academic life.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSignup} className="space-y-2.5 sm:space-y-4">
            <div>
              <label htmlFor="name" className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="John Doe"
                required
                className="w-full bg-white border border-slate-255 focus:border-purple-600 rounded-none px-4 py-3 text-sm focus:ring-4 focus:ring-purple-500/10 focus:outline-none transition-all duration-200 placeholder:text-slate-400"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="name@university.edu"
                required
                className="w-full bg-white border border-slate-255 focus:border-purple-600 rounded-none px-4 py-3 text-sm focus:ring-4 focus:ring-purple-500/10 focus:outline-none transition-all duration-200 placeholder:text-slate-400"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Min. 8 characters"
                required
                className="w-full bg-white border border-slate-255 focus:border-purple-600 rounded-none px-4 py-3 text-sm focus:ring-4 focus:ring-purple-500/10 focus:outline-none transition-all duration-200 placeholder:text-slate-400"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                placeholder="••••••••"
                required
                className="w-full bg-white border border-slate-255 focus:border-purple-600 rounded-none px-4 py-3 text-sm focus:ring-4 focus:ring-purple-500/10 focus:outline-none transition-all duration-200 placeholder:text-slate-400"
              />
            </div>

            {error && <p className="text-red-500 text-xs font-bold text-center mt-1">{error}</p>}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-[#FF8D3A] via-[#F254B6] to-[#2D7FF7] text-white py-3 sm:py-3.5 px-4 rounded-none hover:scale-[1.01] active:scale-[0.99] font-bold text-sm transition-all duration-255 disabled:opacity-75 shadow-lg shadow-purple-500/15 mt-1"
            >
              {isLoading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-3 sm:my-5">
            <div className="flex-grow border-t border-slate-200"></div>
            <span className="mx-3 text-slate-400 text-[9px] sm:text-[10px] font-black tracking-widest uppercase">OR</span>
            <div className="flex-grow border-t border-slate-200"></div>
          </div>

          {/* Social Logins */}
          <div className="flex flex-col gap-2 sm:gap-2.5">
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-2.5 border border-slate-200 py-3 px-4 rounded-none bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold transition-all duration-200 shadow-sm"
            >
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22c-.66-.66-1.12-1.37-1.12-2.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              Continue with Google
            </button>
            
            <button
              onClick={handleGithubLogin}
              className="w-full flex items-center justify-center gap-2.5 border border-slate-200 py-3 px-4 rounded-none bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold transition-all duration-200 shadow-sm"
            >
              <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.167 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.164 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
              </svg>
              Continue with GitHub
            </button>
          </div>

          {/* Redirect Link */}
          <div className="text-center text-xs mt-3 sm:mt-6">
            <span className="text-slate-550 font-medium">Already have an account? </span>
            <Link to="/login" className="text-purple-600 hover:text-purple-700 font-extrabold hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Global Animations & Custom Styles ===== */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          50% { transform: translate(15px, -15px) scale(1.05); }
        }
        .animate-blob { animation: blob 10s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }

        @keyframes gradient-slow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-slow { animation: gradient-slow 22s ease-in-out infinite; }
      `}</style>
    </div>
  )
}
